import styles from './ErpText.module.scss';

import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Collapse } from '@work4all/components/lib/components/collapse';
import { TextEditor } from '@work4all/components/lib/input/format-text/TextEditor';
import { Tabs } from '@work4all/components/lib/navigation/tabs/tabs/Tabs';

import { useFormContextPlus } from '../../../../../../form-plus/use-form-context-plus';
import { BPMaskFormValue } from '../../../types';

interface ErpTextProps {
  type: 'HEADER' | 'CLOSING';
  handleTabChange: (_: unknown, tab: string) => void;
  tab: string;
  tabs: {
    label: string;
    value: string;
  }[];
}

export const ErpText: React.FC<ErpTextProps> = ({
  type,
  handleTabChange,
  tab,
  tabs,
}: ErpTextProps) => {
  const { t } = useTranslation();

  return (
    <Collapse
      defaultOpen={true}
      title={
        <>
          {t(`MASK.${type}_TEXT`)}
          <Tooltip title={t(`COMMON.TOOLTIP_TEXT_${type}`)} placement="right">
            <InfoIcon color="primary" style={{ marginLeft: '0.5rem' }} />
          </Tooltip>
        </>
      }
      topIndent
    >
      <Tabs value={tab} handleChange={handleTabChange} tabs={tabs} />

      <div className={styles.root}>
        <ErpTextEditor type={type} tab={tab} />
      </div>
    </Collapse>
  );
};

interface ErpTextEditorProps {
  type: 'HEADER' | 'CLOSING';
  tab: string;
}

const ErpTextEditor = ({ type, tab }: ErpTextEditorProps) => {
  const { watch, setValue } = useFormContextPlus<BPMaskFormValue>();

  const textType = `${tab}_${type}`;
  const defaultErpTexts = watch('defaultErpTexts');

  const value = useMemo(() => {
    if (!defaultErpTexts) return '';

    return defaultErpTexts.find((x) => x.textType === textType)?.text ?? '';
  }, [defaultErpTexts, textType]);

  const onChange = useCallback(
    (newValue: string) => {
      const value = defaultErpTexts.map((x) => {
        if (x.textType === textType) {
          x.text = newValue;
        }

        return x;
      });

      setValue('defaultErpTexts', value, { shouldDirty: true });
    },
    [setValue, defaultErpTexts, textType]
  );

  return (
    <TextEditor value={value} applyZoomLevel={false} onChange={onChange} rtf />
  );
};
